import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import {
	type ComponentProps,
	type ComponentType,
	type PropsWithChildren,
	createContext,
	useContext,
} from 'react'

import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from './ui/dialog'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from './ui/drawer'
import {
	Popover,
	PopoverClose,
	PopoverContent,
	PopoverTrigger,
} from './ui/popover'

interface DrawerDialogContext {
	isDesktop: boolean
}

const mobileDesktopContext = createContext<DrawerDialogContext>(
	{} as DrawerDialogContext,
)

interface createDrawerDialogComponentParams<
	MobileComponentProps,
	DesktopComponentProps,
> {
	MobileComponent: ComponentType<PropsWithChildren<MobileComponentProps>>
	DesktopComponent: ComponentType<PropsWithChildren<DesktopComponentProps>>
	displayName: string
}

const createMobileDesktopComponent = <
	MobileComponentProps,
	DesktopComponentProps,
>({
	MobileComponent,
	DesktopComponent,
	displayName,
}: createDrawerDialogComponentParams<
	MobileComponentProps,
	DesktopComponentProps
>) => {
	const Component = ({
		children,
		...props
	}: PropsWithChildren<MobileComponentProps & DesktopComponentProps>) => {
		const { isDesktop } = useContext(mobileDesktopContext)

		if (isDesktop) {
			return (
				<DesktopComponent {...(props as DesktopComponentProps)}>
					{children}
				</DesktopComponent>
			)
		}

		return (
			<MobileComponent {...(props as MobileComponentProps)}>
				{children}
			</MobileComponent>
		)
	}

	Component.displayName = displayName

	return Component
}

export const DrawerDialogTrigger = createMobileDesktopComponent({
	DesktopComponent: DialogTrigger,
	MobileComponent: DrawerTrigger,
	displayName: 'DrawerDialogTrigger',
})

export const DrawerDialogClose = createMobileDesktopComponent({
	DesktopComponent: DialogClose,
	MobileComponent: DrawerClose,
	displayName: 'DrawerDialogClose',
})

export const DrawerDialogContent = createMobileDesktopComponent({
	DesktopComponent: DialogContent,
	MobileComponent: DrawerContent,
	displayName: 'DrawerDialogContent',
})

export const DrawerDialogHeader = createMobileDesktopComponent({
	DesktopComponent: DialogHeader,
	MobileComponent: DrawerHeader,
	displayName: 'DrawerDialogHeader',
})

export const DrawerDialogTitle = createMobileDesktopComponent({
	DesktopComponent: DialogTitle,
	MobileComponent: DrawerTitle,
	displayName: 'DrawerDialogTitle',
})

export const DrawerDialogDescription = createMobileDesktopComponent({
	DesktopComponent: DialogDescription,
	MobileComponent: DrawerDescription,
	displayName: 'DrawerDialogDescription',
})

export const DrawerDialogFooter = createMobileDesktopComponent({
	DesktopComponent: DialogFooter,
	MobileComponent: DrawerFooter,
	displayName: 'DrawerDialogFooter',
})

export const DrawerDialog = (
	props: PropsWithChildren<
		ComponentProps<typeof Dialog> & ComponentProps<typeof Drawer>
	>,
) => {
	const isDesktop = useMediaQuery('(min-width: 768px)')

	return (
		<mobileDesktopContext.Provider value={{ isDesktop }}>
			{isDesktop ? <Dialog {...props} /> : <Drawer {...props} />}
		</mobileDesktopContext.Provider>
	)
}

export const DrawerPopoverTrigger = createMobileDesktopComponent({
	DesktopComponent: PopoverTrigger,
	MobileComponent: DrawerTrigger,
	displayName: 'DrawerPopoverTrigger',
})

export const DrawerPopoverClose = createMobileDesktopComponent({
	DesktopComponent: PopoverClose,
	MobileComponent: DrawerClose,
	displayName: 'DrawerPopoverClose',
})

export const DrawerPopoverContent = createMobileDesktopComponent({
	DesktopComponent: PopoverContent,
	MobileComponent: DrawerContent,
	displayName: 'DrawerPopoverContent',
})

export const DrawerPopoverHeader = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	MobileComponent: DrawerHeader,
	displayName: 'DrawerPopoverHeader',
})

export const DrawerPopoverTitle = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	MobileComponent: DrawerTitle,
	displayName: 'DrawerPopoverTitle',
})

export const DrawerPopoverDescription = createMobileDesktopComponent({
	DesktopComponent: ({ children }) => children,
	MobileComponent: DrawerDescription,
	displayName: 'DrawerPopoverDescription',
})

export const DrawerPopover = (
	props: PropsWithChildren<
		ComponentProps<typeof Drawer> & ComponentProps<typeof Popover>
	>,
) => {
	const isDesktop = useMediaQuery('(min-width: 768px)')

	return (
		<mobileDesktopContext.Provider value={{ isDesktop }}>
			{isDesktop ? <Popover {...props} /> : <Drawer {...props} />}
		</mobileDesktopContext.Provider>
	)
}
